import { useState, useCallback, useRef, useEffect } from 'react';
import { usePlaySessionStore } from '../stores/playSessionStore';
import { useDiceRollerStore } from '../stores/diceRollerStore';

export type RollMode = 'normal' | 'advantage' | 'disadvantage';

export interface RollResult {
  type: string;
  d20Roll: number;
  modifier: number;
  total: number;
  isNat20: boolean;
  isNat1: boolean;
  advantage?: 'advantage' | 'disadvantage';
  rolls?: number[]; // For advantage/disadvantage, shows both rolls
}

export interface DiceRollerState {
  isRolling: boolean;
  lastRoll: RollResult | null;
  rollMode: RollMode;
}

// Types for arbitrary dice pool rolling
export interface DicePoolEntry {
  sides: number;  // 4, 6, 8, 10, 12, 20, 100
  count: number;  // How many of this die
  keep?: number;  // Optional: how many to keep (for kh/kl)
  keepHighest?: boolean; // true = keep highest, false = keep lowest
}

export interface PoolRollResult {
  pool: DicePoolEntry[];
  modifier: number;
  rolls: { sides: number; results: number[]; kept?: number[]; dropped?: number[] }[];
  total: number;
  breakdown: string;  // "2d6 (3+5) + 1d8 (4) +3 = 15"
}

// Parsed notation result
export interface ParsedNotation {
  valid: boolean;
  pool: DicePoolEntry[];
  modifier: number;
  description: string; // Human-readable description
  error?: string;
}

/**
 * Parse Roll20-style dice notation
 * Supports: 1d20, 2d6+5, 2d20kh1, 2d20kl1, 4d6kh3, 2d6+1d8+3
 */
export function parseNotation(notation: string): ParsedNotation {
  const input = notation.trim().toLowerCase();

  if (!input) {
    return { valid: false, pool: [], modifier: 0, description: '', error: 'Empty input' };
  }

  const pool: DicePoolEntry[] = [];
  let totalModifier = 0;
  const descParts: string[] = [];

  // Split on + and - while preserving the sign
  // This regex splits "2d6+1d8-3+5" into ["2d6", "+1d8", "-3", "+5"]
  const parts = input.match(/[+-]?[^+-]+/g) || [];

  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) continue;

    // Check if it's a dice expression: NdX or NdXkhY or NdXklY
    const diceMatch = trimmed.match(/^([+-])?(\d*)d(\d+)(k[hl](\d*))?$/);

    if (diceMatch) {
      const sign = diceMatch[1] === '-' ? -1 : 1;
      const count = parseInt(diceMatch[2] || '1') * sign;
      const sides = parseInt(diceMatch[3]);
      const keepPart = diceMatch[4]; // e.g., "kh1" or "kl" or "kh3"

      if (count === 0 || sides === 0) {
        return { valid: false, pool: [], modifier: 0, description: '', error: 'Invalid dice count or sides' };
      }

      const entry: DicePoolEntry = {
        sides,
        count: Math.abs(count),
      };

      let keepDesc = '';
      if (keepPart) {
        const keepHighest = keepPart.startsWith('kh');
        const keepCount = parseInt(diceMatch[5] || '1');
        entry.keep = keepCount;
        entry.keepHighest = keepHighest;
        keepDesc = `, keep ${keepHighest ? 'highest' : 'lowest'}${keepCount > 1 ? ` ${keepCount}` : ''}`;
      }

      pool.push(entry);
      descParts.push(`Roll ${Math.abs(count)}d${sides}${keepDesc}`);
    } else {
      // Check if it's a plain modifier: +5, -3, 5
      const modMatch = trimmed.match(/^([+-])?(\d+)$/);
      if (modMatch) {
        const sign = modMatch[1] === '-' ? -1 : 1;
        const value = parseInt(modMatch[2]) * sign;
        totalModifier += value;
      } else {
        return { valid: false, pool: [], modifier: 0, description: '', error: `Invalid: "${trimmed}"` };
      }
    }
  }

  if (pool.length === 0 && totalModifier === 0) {
    return { valid: false, pool: [], modifier: 0, description: '', error: 'No dice or modifier found' };
  }

  // Build description
  let description = descParts.join(', ');
  if (totalModifier !== 0) {
    description += description ? `, ${totalModifier >= 0 ? '+' : ''}${totalModifier}` : `Modifier: ${totalModifier >= 0 ? '+' : ''}${totalModifier}`;
  }

  return { valid: true, pool, modifier: totalModifier, description };
}

export function useDiceRoller() {
  const [state, setState] = useState<Omit<DiceRollerState, 'rollMode'>>({
    isRolling: false,
    lastRoll: null,
  });

  const { rollMode, setRollMode } = useDiceRollerStore();
  const addRoll = usePlaySessionStore((s) => s.addRoll);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const roll = useCallback(
    (type: string, modifier: number): RollResult => {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      const rollMode = useDiceRollerStore.getState().rollMode;

      let d20Roll: number;
      let rolls: number[] | undefined;

      if (rollMode === 'advantage') {
        const roll1 = Math.floor(Math.random() * 20) + 1;
        const roll2 = Math.floor(Math.random() * 20) + 1;
        d20Roll = Math.max(roll1, roll2);
        rolls = [roll1, roll2];
      } else if (rollMode === 'disadvantage') {
        const roll1 = Math.floor(Math.random() * 20) + 1;
        const roll2 = Math.floor(Math.random() * 20) + 1;
        d20Roll = Math.min(roll1, roll2);
        rolls = [roll1, roll2];
      } else {
        d20Roll = Math.floor(Math.random() * 20) + 1;
      }

      const total = d20Roll + modifier;
      const isNat20 = d20Roll === 20;
      const isNat1 = d20Roll === 1;

      const result: RollResult = {
        type,
        d20Roll,
        modifier,
        total,
        isNat20,
        isNat1,
        advantage: rollMode !== 'normal' ? rollMode : undefined,
        rolls,
      };

      // Update state and trigger animation
      setState((s) => ({
        ...s,
        isRolling: true,
        lastRoll: result,
      }));

      // Add to roll history
      addRoll({
        type,
        d20Roll,
        modifier,
        total,
        advantage: rollMode !== 'normal' ? rollMode : undefined,
      });

      // Play sound effects (if available)
      playRollSound(isNat20, isNat1);

      // Haptic feedback on mobile
      if (navigator.vibrate) {
        if (isNat20) {
          navigator.vibrate([100, 50, 100, 50, 200]); // Success pattern
        } else if (isNat1) {
          navigator.vibrate([300]); // Failure pattern
        } else {
          navigator.vibrate(50); // Normal roll
        }
      }

      // Clear rolling state after animation
      timeoutRef.current = setTimeout(() => {
        setState((s) => ({ ...s, isRolling: false }));
      }, 1500);

      return result;
    },
    [addRoll]
  );

  const rollDamage = useCallback((dice: string): { total: number; rolls: number[]; breakdown: string } => {
    // Parse dice notation like "2d6+5" or "1d8+3"
    const match = dice.match(/^(\d+)?d(\d+)(?:([+-])(\d+))?$/i);
    if (!match) {
      return { total: 0, rolls: [], breakdown: 'Invalid dice notation' };
    }

    const numDice = parseInt(match[1] || '1');
    const dieSize = parseInt(match[2]);
    const modSign = match[3] || '+';
    const modifier = parseInt(match[4] || '0') * (modSign === '-' ? -1 : 1);

    const rolls: number[] = [];
    let diceTotal = 0;

    for (let i = 0; i < numDice; i++) {
      const roll = Math.floor(Math.random() * dieSize) + 1;
      rolls.push(roll);
      diceTotal += roll;
    }

    const total = diceTotal + modifier;
    const modStr = modifier !== 0 ? ` ${modifier >= 0 ? '+' : ''}${modifier}` : '';
    const breakdown = `${numDice}d${dieSize} (${rolls.join('+')})${modStr} = ${total}`;

    // Haptic feedback
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }

    return { total, rolls, breakdown };
  }, []);

  /**
   * Roll an arbitrary pool of dice with optional modifier
   * e.g., [{sides: 6, count: 2}, {sides: 8, count: 1}] with modifier 3 = 2d6+1d8+3
   * Supports keep highest/lowest: [{sides: 20, count: 2, keep: 1, keepHighest: true}] = 2d20kh1
   */
  const rollPool = useCallback((pool: DicePoolEntry[], modifier: number = 0): PoolRollResult => {
    const rolls: { sides: number; results: number[]; kept?: number[]; dropped?: number[] }[] = [];
    let total = 0;

    // Roll each group of dice
    for (const entry of pool) {
      const results: number[] = [];
      for (let i = 0; i < entry.count; i++) {
        const roll = Math.floor(Math.random() * entry.sides) + 1;
        results.push(roll);
      }

      // Handle keep highest/lowest
      if (entry.keep !== undefined && entry.keep < entry.count) {
        const sorted = [...results].sort((a, b) => b - a); // Descending
        const kept = entry.keepHighest
          ? sorted.slice(0, entry.keep)
          : sorted.slice(-entry.keep);
        const dropped = results.filter((r) => {
          // Find first occurrence of this value in kept and remove it
          const keptIndex = kept.indexOf(r);
          if (keptIndex !== -1) {
            kept.splice(keptIndex, 1);
            return false;
          }
          return true;
        });
        // Recalculate kept (we modified it above)
        const keptFinal = entry.keepHighest
          ? [...results].sort((a, b) => b - a).slice(0, entry.keep)
          : [...results].sort((a, b) => a - b).slice(0, entry.keep);

        rolls.push({ sides: entry.sides, results, kept: keptFinal, dropped });
        total += keptFinal.reduce((sum, r) => sum + r, 0);
      } else {
        rolls.push({ sides: entry.sides, results });
        total += results.reduce((sum, r) => sum + r, 0);
      }
    }

    // Add modifier
    total += modifier;

    // Build breakdown string with keep notation
    const parts: string[] = [];
    for (let i = 0; i < rolls.length; i++) {
      const r = rolls[i];
      const entry = pool[i];
      let diceNotation = `${r.results.length}d${r.sides}`;
      if (entry.keep !== undefined) {
        diceNotation += entry.keepHighest ? `kh${entry.keep}` : `kl${entry.keep}`;
      }

      if (r.kept && r.dropped && r.dropped.length > 0) {
        // Show kept in bold/normal, dropped in strikethrough style
        // Use a copy of kept array to avoid mutating the original
        const keptCopy = [...r.kept];
        const rollStr = r.results.map((val) => {
          const keptIndex = keptCopy.indexOf(val);
          if (keptIndex !== -1) {
            keptCopy.splice(keptIndex, 1);
            return val.toString();
          }
          return `~${val}~`;
        }).join(', ');
        parts.push(`${diceNotation} [${rollStr}]`);
      } else {
        const rollStr = r.results.length > 1 ? r.results.join('+') : r.results[0]?.toString() || '0';
        parts.push(`${diceNotation} (${rollStr})`);
      }
    }
    let breakdown = parts.join(' + ');
    if (modifier !== 0) {
      breakdown += modifier > 0 ? ` +${modifier}` : ` ${modifier}`;
    }
    breakdown += ` = ${total}`;

    // Play sound and haptic
    playPoolRollSound();
    if (navigator.vibrate) {
      navigator.vibrate([30, 20, 30, 20, 50]); // Dice tumble pattern
    }

    return { pool, modifier, rolls, total, breakdown };
  }, []);

  const clearLastRoll = useCallback(() => {
    setState((s) => ({ ...s, lastRoll: null, isRolling: false }));
  }, []);

  return {
    ...state,
    rollMode,
    roll,
    rollDamage,
    rollPool,
    setRollMode,
    clearLastRoll,
  };
}

// Sound effects using Web Audio API
let audioContext: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;

  if (!audioContext) {
    try {
      audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    } catch {
      return null;
    }
  }
  return audioContext;
}

function playRollSound(isNat20: boolean, isNat1: boolean): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  // Resume context if suspended (required by browsers)
  if (ctx.state === 'suspended') {
    ctx.resume();
  }

  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();

  oscillator.connect(gainNode);
  gainNode.connect(ctx.destination);

  if (isNat20) {
    // Triumphant ascending tone
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(440, ctx.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.2);
    gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.3);
  } else if (isNat1) {
    // Descending failure tone
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(440, ctx.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(220, ctx.currentTime + 0.3);
    gainNode.gain.setValueAtTime(0.2, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.3);
  } else {
    // Normal click sound
    oscillator.type = 'triangle';
    oscillator.frequency.setValueAtTime(600, ctx.currentTime);
    gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.1);
  }
}

// Sound for pool/arbitrary dice rolls
function playPoolRollSound(): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  if (ctx.state === 'suspended') {
    ctx.resume();
  }

  // Multiple quick clicks to simulate dice tumbling
  for (let i = 0; i < 3; i++) {
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.type = 'triangle';
    // Slightly randomized frequency for each "click"
    oscillator.frequency.setValueAtTime(400 + Math.random() * 300, ctx.currentTime + i * 0.08);
    gainNode.gain.setValueAtTime(0.08, ctx.currentTime + i * 0.08);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.08 + 0.05);
    oscillator.start(ctx.currentTime + i * 0.08);
    oscillator.stop(ctx.currentTime + i * 0.08 + 0.05);
  }
}
